/**
 * A deliberately awkward payload, exactly as a real backend might return it:
 * snake_case keys, numeric ids, one nested `children` block, an orphan whose
 * parent does not exist, and a duplicate id. The explorer has to cope with all
 * of it without the app transforming anything first.
 */
export interface ApiDocument {
  document_id: number;
  document_title: string;
  kind: 'DIRECTORY' | 'FILE';
  folder_id: number | null;
  file_size?: number;
  modified?: string;
  mime?: string;
  preview_url?: string;
  download?: string;
  extra?: { owner?: string; status?: string; department?: string };
  children?: ApiDocument[];
}

export const apiResponse: ApiDocument[] = [
  {
    document_id: 1,
    document_title: 'Engineering',
    kind: 'DIRECTORY',
    folder_id: null,
    modified: '2026-08-22T09:00:00Z',
    extra: { owner: 'Nitesh', department: 'Engineering' },
    children: [
      {
        document_id: 10,
        document_title: 'Architecture',
        kind: 'DIRECTORY',
        folder_id: 1,
        modified: '2026-08-22T10:30:00Z',
        extra: { owner: 'Nitesh' },
        children: [
          {
            document_id: 100,
            document_title: 'system-design.pdf',
            kind: 'FILE',
            folder_id: 10,
            file_size: 2_450_000,
            mime: 'application/pdf',
            modified: '2026-08-22T10:00:00Z',
            preview_url: '/documents/100',
            download: '/documents/100/download',
            extra: { owner: 'Nitesh', status: 'approved' },
          },
          {
            document_id: 101,
            document_title: 'api-spec.docx',
            kind: 'FILE',
            folder_id: 10,
            file_size: 1_200_000,
            modified: '2026-08-21T14:00:00Z',
            download: '/documents/101/download',
            extra: { owner: 'Ada Lovelace', status: 'draft' },
          },
          {
            document_id: 102,
            document_title: 'diagram.png',
            kind: 'FILE',
            folder_id: 10,
            file_size: 340_000,
            mime: 'image/png',
            modified: '2026-08-18T08:00:00Z',
            extra: { owner: 'Ada Lovelace', status: 'approved' },
          },
        ],
      },
      {
        document_id: 11,
        document_title: 'RFC',
        kind: 'DIRECTORY',
        folder_id: 1,
        modified: '2026-08-20T12:00:00Z',
        children: [
          {
            document_id: 110,
            document_title: 'rfc-001-storage.md',
            kind: 'FILE',
            folder_id: 11,
            file_size: 14_500,
            modified: '2026-08-20T12:00:00Z',
            extra: { owner: 'Grace Hopper', status: 'review' },
          },
          {
            document_id: 111,
            document_title: 'rfc-002-auth.md',
            kind: 'FILE',
            folder_id: 11,
            file_size: 9_800,
            modified: '2026-07-30T12:00:00Z',
            extra: { owner: 'Grace Hopper', status: 'approved' },
          },
        ],
      },
      {
        document_id: 12,
        document_title: 'README.md',
        kind: 'FILE',
        folder_id: 1,
        file_size: 8_192,
        modified: '2026-08-19T16:20:00Z',
        extra: { owner: 'Nitesh', status: 'approved' },
      },
      {
        document_id: 13,
        document_title: 'build-pipeline.yaml',
        kind: 'FILE',
        folder_id: 1,
        file_size: 4_100,
        modified: '2026-08-12T11:00:00Z',
        extra: { owner: 'Nitesh', status: 'approved' },
      },
    ],
  },
  {
    document_id: 2,
    document_title: 'Product',
    kind: 'DIRECTORY',
    folder_id: null,
    modified: '2026-08-21T09:00:00Z',
    extra: { owner: 'Ada Lovelace', department: 'Product' },
    children: [
      {
        document_id: 20,
        document_title: 'requirements.docx',
        kind: 'FILE',
        folder_id: 2,
        file_size: 640_000,
        modified: '2026-08-21T09:00:00Z',
        extra: { owner: 'Ada Lovelace', status: 'review' },
      },
      {
        document_id: 21,
        document_title: 'roadmap-2026.xlsx',
        kind: 'FILE',
        folder_id: 2,
        file_size: 88_000,
        modified: '2026-06-02T09:00:00Z',
        extra: { owner: 'Ada Lovelace', status: 'draft' },
      },
      {
        document_id: 22,
        document_title: 'launch-deck.pptx',
        kind: 'FILE',
        folder_id: 2,
        file_size: 5_600_000,
        modified: '2026-08-22T08:15:00Z',
        extra: { owner: 'Ada Lovelace', status: 'draft' },
      },
    ],
  },
  {
    document_id: 3,
    document_title: 'Legal',
    kind: 'DIRECTORY',
    folder_id: null,
    modified: '2026-05-11T09:00:00Z',
    children: [
      {
        document_id: 30,
        document_title: 'msa-signed.pdf',
        kind: 'FILE',
        folder_id: 3,
        file_size: 1_050_000,
        mime: 'application/pdf',
        modified: '2026-05-11T09:00:00Z',
        extra: { owner: 'Grace Hopper', status: 'approved' },
      },
      {
        document_id: 31,
        document_title: 'archive.zip',
        kind: 'FILE',
        folder_id: 3,
        file_size: 42_000_000,
        modified: '2025-12-01T09:00:00Z',
        extra: { owner: 'Grace Hopper' },
      },
    ],
  },

  // --- the awkward rows -------------------------------------------------
  // Parent 9999 does not exist: this should surface at the root, with a warning.
  {
    document_id: 4,
    document_title: 'orphaned-note.txt',
    kind: 'FILE',
    folder_id: 9999,
    file_size: 512,
    modified: '2026-08-15T09:00:00Z',
  },
  // Reuses id 4: this row should be dropped, with a warning.
  { document_id: 4, document_title: 'duplicate-id.txt', kind: 'FILE', folder_id: null },
];

/** The mapper that teaches the explorer this backend's vocabulary. */
export const apiMapper = {
  id: (d: ApiDocument) => d.document_id,
  name: (d: ApiDocument) => d.document_title,
  type: (d: ApiDocument) => (d.kind === 'DIRECTORY' ? 'folder' : 'file'),
  parentId: (d: ApiDocument) => d.folder_id,
  size: 'file_size',
  updatedAt: 'modified',
  mimeType: 'mime',
  viewUrl: 'preview_url',
  downloadUrl: 'download',
  metadata: 'extra',
} as const;
